import { Fragment, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  Pagination,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@workos-inc/authkit-react';
import { Organisation, User } from '~api';
import { api } from '~portal/api';
import {
  ConfirmActionModal,
  OnboardButton,
  platformLabels,
  UserQuotaDrawer,
  UserRowActions,
  useUserAdminActions,
} from './-user-admin';
import * as styles from './-user-admin.css';

export const Route = createFileRoute('/_protected/admin/users_/subscribers')({
  component: AdminSubscribersPage,
});

const PAGE_SIZE = 50;

/** @desc WorkOS-native membership roles on subscriber organisations — wiring lands later. */
const organisationRoleOptions = [
  { value: 'none', label: 'None' },
  { value: 'admin', label: 'Admin' },
];

type OrgMeta = { name: string; externalId: string | null; createdAt: string | null };

/**
 * @desc One table row: an organisation header, a member beneath it, or an expired invitation
 * whose invitee has no membership row to carry it.
 */
type DisplayUnit =
  | { type: 'header'; org: OrgMeta; memberCount: number }
  | { type: 'member'; org: OrgMeta; user: User.Managed }
  | { type: 'invitation'; org: OrgMeta; invitation: User.ExpiredInvitation };

/** @desc Joins an expired invitation to the membership row of the same address and organisation. */
const invitationKey = (organisationId: string, email: string) =>
  `${organisationId}|${email.trim().toLowerCase()}`;

const internalExternalIds = new Set<string>(
  Organisation.internalOrganisations.map((org) => org.externalId),
);

/**
 * @desc Flattens the external estate into paginated display units: one header per organisation,
 * members beneath. Search matches member email/name and organisation name (an org-name match
 * keeps the whole group) and hides organisations left with no matching members — except an
 * org-name match, whose header still shows. With no active search, organisations without
 * members render as bare headers so the whole estate stays visible.
 */
function buildUnits(
  users: User.Managed[],
  organisations: Organisation.Summary[],
  query: string,
  invitations: User.ExpiredInvitation[] = [],
): DisplayUnit[] {
  const q = query.trim().toLowerCase();
  const filtering = q.length > 0;

  const byName = new Map<
    string,
    { meta: OrgMeta; members: User.Managed[]; invitations: User.ExpiredInvitation[] }
  >();
  for (const org of organisations) {
    if (org.externalId && internalExternalIds.has(org.externalId)) continue;
    byName.set(org.name, {
      meta: { name: org.name, externalId: org.externalId ?? null, createdAt: org.createdAt },
      members: [],
      invitations: [],
    });
  }
  for (const user of users) {
    const entry = byName.get(user.organisation.name) ?? {
      // A membership whose organisation is missing from the listing must still render.
      meta: { name: user.organisation.name, externalId: null, createdAt: null },
      members: [],
      invitations: [],
    };
    entry.members.push(user);
    byName.set(user.organisation.name, entry);
  }

  // Invitations already shown on their invitee's membership row are not repeated as rows.
  const memberKeys = new Set(users.map((user) => invitationKey(user.organisation.id, user.email)));
  for (const invitation of invitations) {
    if (memberKeys.has(invitationKey(invitation.organisation.id, invitation.email))) continue;
    const entry = byName.get(invitation.organisation.name) ?? {
      meta: { name: invitation.organisation.name, externalId: null, createdAt: null },
      members: [],
      invitations: [],
    };
    entry.invitations.push(invitation);
    byName.set(invitation.organisation.name, entry);
  }

  const matchesQuery = (user: User.Managed) =>
    q.length === 0 ||
    user.email.toLowerCase().includes(q) ||
    (user.name ?? '').toLowerCase().includes(q);
  const units: DisplayUnit[] = [];
  for (const { meta, members, invitations: orgInvitations } of [...byName.values()].sort((a, b) =>
    a.meta.name.localeCompare(b.meta.name),
  )) {
    const orgNameMatches = q.length > 0 && meta.name.toLowerCase().includes(q);
    const visible = members.filter((member) => orgNameMatches || matchesQuery(member));
    const visibleInvitations = orgInvitations.filter(
      (invitation) =>
        orgNameMatches || q.length === 0 || invitation.email.toLowerCase().includes(q),
    );
    if (visible.length === 0 && visibleInvitations.length === 0 && filtering && !orgNameMatches) {
      continue;
    }
    units.push({ type: 'header', org: meta, memberCount: visible.length });
    for (const member of visible) units.push({ type: 'member', org: meta, user: member });
    for (const invitation of visibleInvitations) {
      units.push({ type: 'invitation', org: meta, invitation });
    }
  }
  return units;
}

function AdminSubscribersPage() {
  const { user: authUser } = useAuth();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);

  const usersQuery = api.users.list.useQuery();
  const organisationsQuery = api.organisations.list.useQuery();
  const expiredInvitationsQuery = api.users.listExpiredInvitations.useQuery();
  const actions = useUserAdminActions(() => usersQuery.refetch());
  const { error, setError, busyMembershipId, changeRole, startAction } = actions;
  const [quotaUser, setQuotaUser] = useState<User.Managed | null>(null);
  const [reissuing, setReissuing] = useState<User.ExpiredInvitation | null>(null);
  const [reissued, setReissued] = useState<string | null>(null);

  const reissueMutation = api.users.reissueInvitation.useMutation({
    onSuccess: () => {
      setReissued(reissuing?.email ?? null);
      setReissuing(null);
      expiredInvitationsQuery.refetch();
      usersQuery.refetch();
    },
    onError: (err) => {
      setReissuing(null);
      setError(err.message);
    },
  });

  const startReissue = (invitation: User.ExpiredInvitation) => {
    setError(null);
    setReissued(null);
    setReissuing(invitation);
  };

  const subscribers = (usersQuery.data ?? []).filter((user) => user.internalRole === null);
  const expiredInvitations = expiredInvitationsQuery.data ?? [];
  const expiredByMember = new Map(
    expiredInvitations.map((invitation) => [
      invitationKey(invitation.organisation.id, invitation.email),
      invitation,
    ]),
  );
  const units = buildUnits(subscribers, organisationsQuery.data ?? [], query, expiredInvitations);

  const totalPages = Math.max(1, Math.ceil(units.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageUnits = units.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  // A page starting mid-group re-emits its organisation header for orientation.
  const displayUnits: DisplayUnit[] =
    pageUnits[0]?.type === 'member'
      ? [
          {
            type: 'header',
            org: pageUnits[0].org,
            memberCount: units.filter(
              (unit) => unit.type === 'member' && unit.org.name === pageUnits[0]?.org.name,
            ).length,
          },
          ...pageUnits,
        ]
      : pageUnits;

  const resetPage = () => setPage(1);
  const loading = usersQuery.isLoading || organisationsQuery.isLoading;

  return (
    <Stack gap="lg" p="lg">
      <ConfirmActionModal actions={actions} />
      <UserQuotaDrawer user={quotaUser} onClose={() => setQuotaUser(null)} />
      <Modal
        opened={reissuing !== null}
        onClose={() => setReissuing(null)}
        title="Reissue invitation"
        centered
      >
        <Stack gap="md">
          <Text>
            Send a new invitation to{' '}
            <Text span fw={600}>
              {reissuing?.email}
            </Text>{' '}
            to join {reissuing?.organisation.name}? The new invitation is valid for 7 days.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setReissuing(null)}>
              Cancel
            </Button>
            <Button
              loading={reissueMutation.isPending}
              onClick={() => {
                if (reissuing) reissueMutation.mutate({ invitationId: reissuing.invitationId });
              }}
            >
              Reissue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Group justify="space-between">
        <Title order={1}>Subscribers</Title>
        <Button
          onClick={() => {
            usersQuery.refetch();
            organisationsQuery.refetch();
            expiredInvitationsQuery.refetch();
          }}
          loading={
            usersQuery.isFetching ||
            organisationsQuery.isFetching ||
            expiredInvitationsQuery.isFetching
          }
          size="xs"
          variant="light"
        >
          Refresh
        </Button>
      </Group>

      {error && (
        <Alert color="red" title="Error" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {reissued && (
        <Alert
          color="green"
          title="Invitation reissued"
          withCloseButton
          onClose={() => setReissued(null)}
        >
          A new invitation has been sent to {reissued}.
        </Alert>
      )}

      <Paper withBorder p="md">
        <Text size="sm" c="dimmed" mb="md">
          The external estate — every organisation and member outside the staff organisations. Role
          changes take effect on the user&rsquo;s next access token.
        </Text>

        <Group gap="md" mb="md">
          <TextInput
            aria-label="Search subscribers"
            placeholder="Search by email, name or organisation"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(event) => {
              setQuery(event.currentTarget.value);
              resetPage();
            }}
            w={320}
          />
        </Group>

        {units.length === 0 && !loading ? (
          <Text c="dimmed" size="sm">
            No subscribers match
          </Text>
        ) : (
          <>
            <ScrollArea>
              <Table striped highlightOnHover miw={760}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Email</Table.Th>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Agent Beta</Table.Th>
                    <Table.Th>Portal Beta</Table.Th>
                    <Table.Th>Organisation Roles</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayUnits.map((unit, index) =>
                    unit.type === 'header' ? (
                      <Fragment key={`header-${unit.org.name}`}>
                        {index > 0 && (
                          <Table.Tr aria-hidden className={styles.spacerRow}>
                            <Table.Td colSpan={7} />
                          </Table.Tr>
                        )}
                        <Table.Tr data-testid={`org-${unit.org.name}`}>
                          <Table.Td colSpan={7} className={styles.orgHeaderCell}>
                            <Group gap="xs">
                              <Text size="sm" fw={600}>
                                {unit.org.name}
                              </Text>
                              <Badge size="xs" variant="light" color="gray">
                                {unit.memberCount}
                              </Badge>
                              {unit.org.externalId && (
                                <Text size="xs" c="dimmed">
                                  {unit.org.externalId}
                                </Text>
                              )}
                              {unit.org.createdAt && (
                                <Text size="xs" c="dimmed">
                                  created {new Date(unit.org.createdAt).toLocaleDateString()}
                                </Text>
                              )}
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      </Fragment>
                    ) : unit.type === 'invitation' ? (
                      <InvitationRow
                        key={unit.invitation.invitationId}
                        invitation={unit.invitation}
                        onReissue={startReissue}
                      />
                    ) : (
                      <SubscriberRow
                        key={unit.user.membershipId}
                        expiredInvitation={expiredByMember.get(
                          invitationKey(unit.user.organisation.id, unit.user.email),
                        )}
                        onReissue={startReissue}
                        user={unit.user}
                        isSelf={unit.user.workosUserId === authUser?.id}
                        busy={busyMembershipId === unit.user.membershipId}
                        changeRole={changeRole}
                        startAction={startAction}
                        onViewQuota={setQuotaUser}
                      />
                    ),
                  )}
                </Table.Tbody>
              </Table>
            </ScrollArea>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination total={totalPages} value={currentPage} onChange={setPage} />
              </Group>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}

/** @desc A member row of the estate table. */
function SubscriberRow({
  user,
  expiredInvitation,
  onReissue,
  isSelf,
  busy,
  changeRole,
  startAction,
  onViewQuota,
}: {
  user: User.Managed;
  /** @desc The member's lapsed invitation, when they never accepted it. */
  expiredInvitation: User.ExpiredInvitation | undefined;
  onReissue: (invitation: User.ExpiredInvitation) => void;
  isSelf: boolean;
  busy: boolean;
  changeRole: ReturnType<typeof useUserAdminActions>['changeRole'];
  startAction: ReturnType<typeof useUserAdminActions>['startAction'];
  onViewQuota: (user: User.Managed) => void;
}) {
  return (
    <Table.Tr data-testid={`user-${user.workosUserId}`}>
      <Table.Td>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm">{user.email || user.workosUserId}</Text>
          {isSelf && (
            <Badge size="xs" variant="light">
              You
            </Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{user.name ?? '—'}</Text>
      </Table.Td>
      <Table.Td>
        <OnboardButton
          state={user.agentRole === User.roles.AgentBeta ? 'done' : 'actionable'}
          ariaLabel={`Onboard agent for ${user.email}`}
          tooltip="Onboard via Requests if a signup request exists; otherwise onboard here directly"
          onClick={() => changeRole(user, User.roleGroups.Agent, User.roles.AgentBeta)}
          loading={busy}
        />
      </Table.Td>
      <Table.Td>
        {user.platformRole ? (
          // Reserved platform roles are not yet assignable — display only.
          <Badge size="xs" variant="light" color="blue">
            {platformLabels[user.platformRole] ?? user.platformRole}
          </Badge>
        ) : (
          <OnboardButton state="placeholder" ariaLabel={`Onboard platform for ${user.email}`} />
        )}
      </Table.Td>
      <Table.Td>
        {/* Read-only until the org-role backend surface lands: readOnly (not disabled) keeps the
            pointer events the WIP tooltip needs. */}
        <Tooltip label="WIP">
          <Select
            aria-label={`Organisation roles for ${user.email}`}
            size="xs"
            w={110}
            allowDeselect={false}
            data={organisationRoleOptions}
            value={user.otherRoles.includes('admin') ? 'admin' : 'none'}
            readOnly
          />
        </Tooltip>
      </Table.Td>
      <Table.Td>
        {/* A live membership still awaiting product onboarding reads as pending, not active —
            the WorkOS membership status alone overstates where the user is in the funnel. An
            expired invitation outranks both: the member cannot get in until it is reissued. */}
        {expiredInvitation ? (
          <ExpiredInvitationStatus invitation={expiredInvitation} onReissue={onReissue} />
        ) : user.status === 'active' &&
          user.agentRole !== User.roles.AgentBeta &&
          !user.platformRole ? (
          <Badge color="yellow" variant="filled" size="sm">
            pending
          </Badge>
        ) : (
          <Badge color={user.status === 'active' ? 'green' : 'gray'} variant="filled" size="sm">
            {user.status}
          </Badge>
        )}
      </Table.Td>
      <Table.Td>
        <UserRowActions
          user={user}
          isSelf={isSelf}
          busy={busy}
          startAction={startAction}
          onViewQuota={onViewQuota}
        />
      </Table.Td>
    </Table.Tr>
  );
}

/** @desc The Status cell of a lapsed invitation: the expiry, and the action that replaces it. */
function ExpiredInvitationStatus({
  invitation,
  onReissue,
}: {
  invitation: User.ExpiredInvitation;
  onReissue: (invitation: User.ExpiredInvitation) => void;
}) {
  return (
    <Group gap="xs" wrap="nowrap">
      <Tooltip label={`Expired ${new Date(invitation.expiresAt).toLocaleDateString()}`}>
        <Badge color="red" variant="light" size="sm">
          Invite expired
        </Badge>
      </Tooltip>
      <Button
        size="compact-xs"
        variant="light"
        aria-label={`Reissue invitation for ${invitation.email}`}
        onClick={() => onReissue(invitation)}
      >
        Reissue
      </Button>
    </Group>
  );
}

/** @desc An expired invitation whose invitee has no membership row yet. */
function InvitationRow({
  invitation,
  onReissue,
}: {
  invitation: User.ExpiredInvitation;
  onReissue: (invitation: User.ExpiredInvitation) => void;
}) {
  return (
    <Table.Tr data-testid={`invitation-${invitation.invitationId}`}>
      <Table.Td>
        <Text size="sm">{invitation.email}</Text>
      </Table.Td>
      <Table.Td colSpan={4}>
        <Text size="sm" c="dimmed">
          Invited {new Date(invitation.createdAt).toLocaleDateString()}
        </Text>
      </Table.Td>
      <Table.Td>
        <ExpiredInvitationStatus invitation={invitation} onReissue={onReissue} />
      </Table.Td>
      <Table.Td />
    </Table.Tr>
  );
}
